import { Address, EVMAddress, SolanaAddress, TronAddress } from '@src/types/token'

export const NULLADDRESS: EVMAddress = '0x0000000000000000000000000000000000000000' as EVMAddress
export const NULL_CHAINID = -999

export enum ChainId {
    Arbitrum = 42161,
    Avalanche = 43114,
    Celo = 42220,
    Base = 8453,
    Blast = 81457,
    Bsc = 56,
    Ethereum = 1,
    Mantle = 5000,
    Mode = 34443,
    Opbnb = 204,
    Optimism = 10,
    Polygon = 137,
    Scroll = 534352,
    Solana = 900,
    Tron = 728126428,
    Unichain = 130,
    ApeChain = 33139,
    ZkSync = 324,
    Linea = 59144,
    Fantom = 250, // Fantom Opera
    Moonbeam = 1284,
    Moonriver = 1285,
    Harmony = 1666600000,
    Goerli = 5,
    BscTestnet = 97,
    OpGoerli = 420,
    AvalancheFuji = 43113,
    PolygonMumbai = 80001,
    ArbitrumGoerli = 421614,
    Sepolia = 11155111,
    MonadTestnet = 10143,
    MegaEthTestnet = 6342,
}

export type ChainName =
    | 'arbitrum'
    | 'avalanche'
    | 'celo'
    | 'base'
    | 'blast'
    | 'bsc'
    | 'ethereum'
    | 'mantle'
    | 'mode'
    | 'opbnb'
    | 'optimism'
    | 'polygon'
    | 'scroll'
    | 'solana'
    | 'tron'
    | 'unichain'
    | 'apechain'
    | 'sepolia'
    | 'monadtestnet'
    | 'megaethtestnet'

// ChainName -> ReadableChainName
export const CHAIN_NAMES: { [key in ChainName]: string } = {
    arbitrum: 'Arbitrum',
    avalanche: 'Avalanche',
    celo: 'Celo',
    base: 'Base',
    blast: 'Blast',
    bsc: 'BNB Chain',
    ethereum: 'Ethereum',
    mantle: 'Mantle',
    mode: 'Mode',
    opbnb: 'opBNB',
    optimism: 'Optimism',
    polygon: 'Polygon',
    scroll: 'Scroll',
    solana: 'Solana',
    tron: 'Tron',
    unichain: 'Unichain',
    apechain: 'ApeChain',
    sepolia: 'Sepolia',
    monadtestnet: 'Monad Testnet',
    megaethtestnet: 'MegaETH Testnet',
}

// ChainName -> ChainId
export const CHAIN_IDS: { [key in ChainName]: number } = {
    arbitrum: ChainId.Arbitrum,
    avalanche: ChainId.Avalanche,
    celo: ChainId.Celo,
    base: ChainId.Base,
    blast: ChainId.Blast,
    bsc: ChainId.Bsc,
    ethereum: ChainId.Ethereum,
    mantle: ChainId.Mantle,
    mode: ChainId.Mode,
    opbnb: ChainId.Opbnb,
    optimism: ChainId.Optimism,
    polygon: ChainId.Polygon,
    scroll: ChainId.Scroll,
    solana: ChainId.Solana,
    tron: ChainId.Tron,
    unichain: ChainId.Unichain,
    apechain: ChainId.ApeChain,
    sepolia: ChainId.Sepolia,
    monadtestnet: ChainId.MonadTestnet,
    megaethtestnet: ChainId.MegaEthTestnet,
}

// These correspond to the native gas token of the networks
export const NATIVE_TOKENS: { [key in ChainName]: string } = {
    arbitrum: 'ETH',
    avalanche: 'AVAX',
    celo: 'CELO',
    base: 'ETH',
    blast: 'ETH',
    bsc: 'BNB',
    ethereum: 'ETH',
    mantle: 'MNT',
    mode: 'ETH',
    opbnb: 'BNB',
    optimism: 'ETH',
    polygon: 'POL',
    scroll: 'ETH',
    solana: 'SOL',
    tron: 'TRX',
    unichain: 'ETH',
    apechain: 'APE',
    monadtestnet: 'MON',
    sepolia: 'ETH',
    megaethtestnet: 'ETH',
}

// These are the chain IDs for the testnets
// Used in usePrecomputedGasPrices.ts to forego gas price computations for mainnets
export const TESTNET_CHAIN_IDS: { [key: string]: number } = {
    monadtestnet: ChainId.MonadTestnet,
    sepolia: ChainId.Sepolia,
    megaethtestnet: ChainId.MegaEthTestnet,
}

export const getChainName = (key: ChainName): string => CHAIN_NAMES[key] || 'Unknown Chain'
export const getChainId = (key: ChainName): number => CHAIN_IDS[key] || NULL_CHAINID

// Get chain name for destination wallet
export const getChainType = (chainId: ChainId): 'EVM' | 'Solana' | 'Tron' => {
    if (chainId === ChainId.Solana) return 'Solana'
    if (chainId === ChainId.Tron) return 'Tron'
    return 'EVM'
}

export const getChainNameFromChainId = (chainId: ChainId): ChainName => {
    const chainName = Object.keys(CHAIN_IDS).find(chain => CHAIN_IDS[chain as ChainName] === chainId) as ChainName

    return chainName || 'Unknown Chain'
}

export const getGasTokenSymbol = (key: ChainName | ChainId): string => {
    if (typeof key === 'number') {
        // Find the string key that corresponds to the number in CHAIN_IDS
        const chainName = Object.keys(CHAIN_IDS).find(chain => CHAIN_IDS[chain as ChainName] === key) as
            | ChainName
            | undefined
        if (chainName) {
            return NATIVE_TOKENS[chainName] || 'Unknown'
        }
    } else if (typeof key === 'string') {
        // Directly use the string key
        return NATIVE_TOKENS[key] || 'Unknown'
    }
    return 'Unknown'
}

export const isTestnet = (chainId: ChainId): boolean => {
    return [ChainId.ArbitrumGoerli, ChainId.Sepolia, ChainId.MonadTestnet, ChainId.MegaEthTestnet].includes(chainId)
}

export const WRAPPED_GAS_TOKENS: Readonly<Record<number, { address: Address; decimals: number }>> = {
    [ChainId.Ethereum]: {
        address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Polygon]: {
        address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Bsc]: {
        address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Avalanche]: {
        address: '0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Arbitrum]: {
        address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Unichain]: {
        address: '0x4200000000000000000000000000000000000006'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Optimism]: {
        address: '0x4200000000000000000000000000000000000006'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Base]: {
        address: '0x4200000000000000000000000000000000000006'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.ZkSync]: {
        address: '0x5AEa5775959fBC2557Cc8789bC1bf90A239D9a91'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Linea]: {
        address: '0xe5D7C2a44FfDDf6b295A15c148167daaAf5Cf34f'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Scroll]: {
        address: '0x5300000000000000000000000000000000000004'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Fantom]: {
        address: '0x21be370D5312f44cB42ce377BC9b8a0cEF1A4C83'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Moonbeam]: {
        address: '0xAcc15dC74880C9944775448304B263D191c6077F'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Moonriver]: {
        address: '0x98878B06940aE243284CA214f92Bb71a2b032B8A'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Celo]: {
        address: '0x471EcE3750Da237f93B8E339c536989b8978a438'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Harmony]: {
        address: '0xcF664087a5bB0237a0BAd6742852ec6c8d69A27a'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Blast]: {
        address: '0x4300000000000000000000000000000000000004'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Mantle]: {
        address: '0x78c1b0c915c4faa5fffa6cabf0219da63d7f4cb8'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Opbnb]: {
        address: '0x4200000000000000000000000000000000000006'.toLowerCase() as EVMAddress,
        decimals: 18,
    },
    [ChainId.Tron]: {
        address: 'TNUC9Qb1rRpS5CbWLmNMxXBjyFoydXjWFR' as TronAddress,
        decimals: 6,
    },
    [ChainId.Solana]: {
        address: 'So11111111111111111111111111111111111111112' as SolanaAddress,
        decimals: 9,
    },
    [ChainId.MonadTestnet]: {
        address: '0x760AfE86e5de5fa0Ee542fc7B7B713e1c5425701' as EVMAddress,
        decimals: 18,
    },
    [ChainId.Sepolia]: {
        address: '0x7b79995e5f793A07Bc00c21412e50Ecae098E7f9' as EVMAddress,
        decimals: 18,
    },
    [ChainId.ApeChain]: {
        address: '0x48b62137EdfA95a428D35C09E44256a739F6B557' as EVMAddress,
        decimals: 18,
    },
    [ChainId.MegaEthTestnet]: {
        address: '0x4eB2Bd7beE16F38B1F4a0A5796Fffd028b6040e9' as EVMAddress,
        decimals: 18,
    },
}

export const NATIVE_TOKEN_ADDRESS: Readonly<Record<number, Address>> = {
    [ChainId.Ethereum]: NULLADDRESS,
    [ChainId.Polygon]: NULLADDRESS,
    [ChainId.Bsc]: NULLADDRESS,
    [ChainId.Avalanche]: NULLADDRESS,
    [ChainId.Arbitrum]: NULLADDRESS,
    [ChainId.Unichain]: NULLADDRESS,
    [ChainId.Optimism]: NULLADDRESS,
    [ChainId.Base]: NULLADDRESS,
    [ChainId.ZkSync]: NULLADDRESS,
    [ChainId.Linea]: NULLADDRESS,
    [ChainId.Scroll]: NULLADDRESS,
    [ChainId.Fantom]: NULLADDRESS,
    [ChainId.Moonbeam]: NULLADDRESS,
    [ChainId.Moonriver]: NULLADDRESS,
    [ChainId.Celo]: '0x471EcE3750Da237f93B8E339c536989b8978a438' as Address,
    [ChainId.Harmony]: NULLADDRESS,
    [ChainId.Blast]: NULLADDRESS,
    [ChainId.Mantle]: NULLADDRESS,
    [ChainId.Opbnb]: NULLADDRESS,
    [ChainId.Tron]: NULLADDRESS,
    [ChainId.Solana]: NULLADDRESS,
    [ChainId.MonadTestnet]: NULLADDRESS,
    [ChainId.Sepolia]: NULLADDRESS,
    [ChainId.ApeChain]: NULLADDRESS,
    [ChainId.MegaEthTestnet]: NULLADDRESS,
}

export const LZ_CHAIN_IDS: Readonly<Record<number, number>> = {
    // --------------------- LZ V1 Chain IDs ---------------------
    [ChainId.Arbitrum]: 30110,
    [ChainId.Avalanche]: 30106,
    [ChainId.Celo]: 30125,
    [ChainId.Base]: 30184,
    [ChainId.Blast]: 30243,
    [ChainId.Bsc]: 30102,
    [ChainId.Ethereum]: 30101,
    [ChainId.Mantle]: 30181,
    [ChainId.Mode]: 30260,
    [ChainId.Opbnb]: 30202,
    [ChainId.Optimism]: 30111,
    [ChainId.Polygon]: 30109,
    [ChainId.Scroll]: 30214,

    [ChainId.Goerli]: 10121,
    [ChainId.BscTestnet]: 10102,
    [ChainId.OpGoerli]: 10132,
    [ChainId.AvalancheFuji]: 10106,
    [ChainId.PolygonMumbai]: 10143,
    [ChainId.ArbitrumGoerli]: 10143,
    [ChainId.Sepolia]: 40161,
    [ChainId.MonadTestnet]: 40204,
    [ChainId.MegaEthTestnet]: 40370,

    // --------------------- LZ V2 Chain IDs ---------------------
    [ChainId.Solana]: 30168,
    [ChainId.Tron]: 30420,
    [ChainId.Unichain]: 30320,
    [ChainId.ApeChain]: 30312,
}
